import * as React from "react";

import { Box } from "../../components/ui/box";
import { cn } from "../../lib/utils";

import {
  STRUCTURAL_COLUMN_IDS,
  TABLE_TOTAL_WIDTH_VAR,
  columnLeftVar,
  columnWidthVar,
} from "./data-table-utils";
import type { Omittable } from "../../lib/props";

// ---------------------------------------------------------------------------
// useColumnResize — drag controller for column resizing
// ---------------------------------------------------------------------------
//
// Width = clamp(startWidth + (clientX − startX)) — absolute math from a
// drag-start snapshot of the resolved width map, so the dragged boundary
// tracks the pointer 1:1 and clamped overshoot recovers without drift.
//
// Per-frame updates write CSS variables on the scroll container imperatively
// (the dragged column's width var, downstream pinned left-offset vars, and
// the table total) — zero React renders per frame. State commits once on
// pointer-up via onCommit.
//
// First resize bakes the table: when the sizing map is empty, the resolved
// map (minus structural columns AND minus the absorber column) is committed
// alongside the dragged width. Baked values equal the currently rendered
// widths, so nothing moves — it freezes auto flex so no non-absorber column
// ever reflows. The absorber is deliberately left unbaked: it is the elastic
// column, and persisting its container-dependent stretched width would
// freeze it (and change which column flexes on the next load).
//
// The absorber must sit visually AFTER the dragged column. Shrinking a
// column on the other side of the dragged boundary shifts everything between
// it and the boundary, so the edge under the pointer wouldn't move — resize
// reads as the dragged column "growing out of the left". When the designated
// fill column is at or before the dragged one (e.g. a pinned first column as
// fill), the visually-last consumer column absorbs instead; the fill column
// then IS baked at its rendered (stretched) width, which both keeps the
// commit re-render identical to the last drag frame and drops the fill
// designation (sizing[fillColumnId] set → data-table falls back to the
// visually-last column), so subsequent drags stay well-behaved.
//
// Packed layout drags run with NO absorber: nothing clamps the table edge, so
// it simply follows the pointer. An absorber there would stretch mid-drag to
// the container (the edge jumping out, sticky right lanes with it) and snap
// back on release. Such a drag writes only its own width and the table total,
// and its first-resize bake freezes every other consumer column.

export type ColumnResizeController = {
  startResize: (columnId: string, clientX: number) => void;
  moveResize: (clientX: number) => void;
  endResize: () => void;
  clearColumnSize: (columnId: string) => void;
};

type ActiveDrag = {
  columnId: string;
  startX: number;
  startWidth: number;
  startTotal: number;
  /** Full-map snapshot to commit when this drag is the first customization. */
  bake: Record<string, number> | null;
  /** Left-pinned columns after the dragged one — their sticky offsets follow the drag. */
  downstreamPinned: { id: string; startOffset: number }[];
  /**
   * The drag's absorber column — absorbs any width deficit per frame so the
   * table edge stays glued to the container while shrinking. The designated
   * fill column when it sits visually after the dragged one, otherwise the
   * visually-last consumer column (see the header comment). baseWidth is the
   * unstretched width; startStretch is the stretch already applied at drag
   * start (resolved − base), so the natural total can be recovered. `null`
   * when no consumer column sits after the dragged one (packed layout's
   * last column) — the edge then moves freely.
   */
  fill: { id: string; baseWidth: number; startStretch: number } | null;
  /** The dragged column's width bounds, snapshotted at drag start — every
   *  frame clamps into them, so the commit can never land out of range. */
  min: number;
  max: number;
  containerWidth: number;
  lastWidth: number;
  /** The queued frame's handle, 0 when none is queued. Zero is a safe
   *  sentinel because animation-frame handles are spec'd greater than zero,
   *  so cancelling it is always a no-op — which is what lets the move and
   *  release paths cancel unconditionally instead of guarding on a null. */
  raf: number;
  pendingClientX: number | null;
  el: HTMLElement;
  prevCursor: string;
  prevUserSelect: string;
};

export function useColumnResize({
  scrollContainerRef,
  widths,
  pinnedLeftIds,
  sizing,
  fillColumn,
  packed = false,
  widthLimits,
  onCommit,
}: Omittable<{
  scrollContainerRef: React.RefObject<HTMLElement | null>;
  widths: Map<string, number>;
  pinnedLeftIds: string[];
  sizing: Record<string, number>;
  /** The table's fill column + its unstretched width (see ActiveDrag.fill). */
  fillColumn?: { id: string; baseWidth: number } | null;
  /** Packed column layout — drags take no absorber (see the header comment). */
  packed?: boolean;
  /** Per-column drag bounds (see resolveColumnWidthLimits). Keyed by every
   *  resolvable column id, like `widths`. */
  widthLimits: Map<string, { min: number; max: number }>;
  onCommit: (sizing: Record<string, number>) => void;
}>): {
  controller: ColumnResizeController;
  activeResizeColumnId: string | null;
} {
  const widthsRef = React.useRef(widths);
  widthsRef.current = widths;
  const pinnedLeftIdsRef = React.useRef(pinnedLeftIds);
  pinnedLeftIdsRef.current = pinnedLeftIds;
  const sizingRef = React.useRef(sizing);
  sizingRef.current = sizing;
  const fillColumnRef = React.useRef(fillColumn);
  fillColumnRef.current = fillColumn;
  const packedRef = React.useRef(packed);
  packedRef.current = packed;
  const widthLimitsRef = React.useRef(widthLimits);
  widthLimitsRef.current = widthLimits;
  const onCommitRef = React.useRef(onCommit);
  onCommitRef.current = onCommit;

  const dragRef = React.useRef<ActiveDrag | null>(null);
  const [activeResizeColumnId, setActiveResizeColumnId] = React.useState<
    string | null
  >(null);

  const startResize = React.useCallback(
    (columnId: string, clientX: number) => {
      // The one and only caller is `DataTableResizeHandle`, which data-table
      // renders once per resizable, measured, non-structural column, inside
      // the scroll container. Three invariants follow, and each replaces a
      // guard that no caller could trip:
      //  - `columnId` is never structural (`canResize` excludes those),
      //  - it is always a key of the resolved width map (the header cells and
      //    that map are both built from `visualLayout.visualIds`),
      //  - the scroll container is mounted, because the handle is inside it.
      const currentWidths = widthsRef.current;
      const startWidth = currentWidths.get(columnId)!;
      const el = scrollContainerRef.current as HTMLElement;

      let startTotal = 0;
      for (const w of currentWidths.values()) startTotal += w;

      const currentFill = fillColumnRef.current;
      // Pick the drag's absorber: the designated fill column only when it
      // sits visually after the dragged one — shrinking a column before the
      // dragged boundary would move the edge under the pointer (see the
      // header comment). Otherwise the visually-last consumer column after
      // the dragged one absorbs. currentWidths preserves visual order (left
      // pinned → center → right pinned), so key index IS visual position.
      const visualIds = [...currentWidths.keys()];
      const draggedIndex = visualIds.indexOf(columnId);
      let found: { id: string; baseWidth: number } | null = null;
      if (packedRef.current) {
        // No absorber — see the header comment.
      } else if (
        currentFill &&
        currentFill.id !== columnId &&
        visualIds.indexOf(currentFill.id) > draggedIndex
      ) {
        found = currentFill;
      } else {
        for (let i = visualIds.length - 1; i > draggedIndex; i--) {
          const id = visualIds[i];
          if (id === undefined || STRUCTURAL_COLUMN_IDS.has(id)) continue;
          // Not the designated fill, so its resolved width carries no
          // stretch — the map value is the unstretched base.
          found = { id, baseWidth: currentWidths.get(id)! };
          break;
        }
      }
      // `found` is null in packed layout, and for the visually-last consumer
      // column (fill layout withholds its seam via `lastResizableId`).
      const absorber = found;

      const currentSizing = sizingRef.current;
      let bake: Record<string, number> | null = null;
      if (Object.keys(currentSizing).length === 0) {
        bake = {};
        for (const [id, w] of currentWidths) {
          // Skip the absorber — its resolved width may include a
          // container-dependent stretch, and persisting that would freeze
          // the table's elastic column (see the header comment). A fill
          // column that is NOT the absorber is baked at its rendered width
          // on purpose: that freezes it and hands fill duty to the
          // visually-last column from the commit onward.
          if (STRUCTURAL_COLUMN_IDS.has(id) || id === absorber?.id) {
            continue;
          }
          bake[id] = Math.round(w);
        }
      }

      const downstreamPinned: { id: string; startOffset: number }[] = [];
      const leftIds = pinnedLeftIdsRef.current;
      const draggedPinnedIndex = leftIds.indexOf(columnId);
      if (draggedPinnedIndex !== -1) {
        let offset = 0;
        for (const [i, leftId] of leftIds.entries()) {
          if (i > draggedPinnedIndex) {
            downstreamPinned.push({ id: leftId, startOffset: offset });
          }
          // Left-pinned ids are a subset of the map's keys, so no fallback.
          offset += currentWidths.get(leftId)!;
        }
      }

      const fill = absorber
        ? {
            id: absorber.id,
            baseWidth: absorber.baseWidth,
            startStretch:
              currentWidths.get(absorber.id)! - absorber.baseWidth,
          }
        : null;

      // Same invariant as the width map: the limits map is keyed by
      // `visualLayout.visualIds`, and only those columns render a handle.
      const limits = widthLimitsRef.current.get(columnId)!;

      const body = document.body;
      dragRef.current = {
        columnId,
        startX: clientX,
        startWidth,
        startTotal,
        min: limits.min,
        max: limits.max,
        bake,
        downstreamPinned,
        fill,
        containerWidth: el.clientWidth,
        lastWidth: startWidth,
        raf: 0,
        pendingClientX: null,
        el,
        prevCursor: body.style.cursor,
        prevUserSelect: body.style.userSelect,
      };
      body.style.cursor = "col-resize";
      body.style.userSelect = "none";
      setActiveResizeColumnId(columnId);
    },
    [scrollContainerRef],
  );

  const applyFrame = React.useCallback(() => {
    const drag = dragRef.current;
    // This guard is what lets `endResize` flush unconditionally: a release
    // whose last move already painted arrives here with nothing pending and
    // must be a no-op. The no-drag arm is the frame-timing backstop —
    // `endResize` cancels the queued frame and then clears the drag
    // snapshot, but a cancel cannot un-queue a callback the browser has
    // already begun dispatching, and re-entering from one must not write
    // from a stale snapshot.
    if (!drag || drag.pendingClientX === null) return;
    drag.raf = 0;
    const clientX = drag.pendingClientX;
    drag.pendingClientX = null;

    const next = Math.max(
      drag.min,
      Math.min(drag.max, drag.startWidth + (clientX - drag.startX)),
    );
    drag.lastWidth = next;

    const style = drag.el.style;
    const delta = next - drag.startWidth;
    style.setProperty(columnWidthVar(drag.columnId), `${next}px`);
    if (drag.fill) {
      // Mirror the commit-time fill: recover the natural (unstretched) total,
      // give the fill column exactly the remaining deficit, never less than
      // its base. Keeps the table edge glued to the container mid-drag with
      // no jump on release.
      const naturalTotal = drag.startTotal + delta - drag.fill.startStretch;
      const deficit = Math.max(0, drag.containerWidth - naturalTotal);
      style.setProperty(
        columnWidthVar(drag.fill.id),
        `${drag.fill.baseWidth + deficit}px`,
      );
      style.setProperty(TABLE_TOTAL_WIDTH_VAR, `${naturalTotal + deficit}px`);
    } else {
      // No absorber (packed layout's last column): the table edge follows the
      // dragged edge, so the total is just the start total plus the delta.
      style.setProperty(TABLE_TOTAL_WIDTH_VAR, `${drag.startTotal + delta}px`);
    }
    for (const { id, startOffset } of drag.downstreamPinned) {
      style.setProperty(columnLeftVar(id), `${startOffset + delta}px`);
    }
  }, []);

  const moveResize = React.useCallback(
    (clientX: number) => {
      const drag = dragRef.current;
      if (!drag) return;
      drag.pendingClientX = clientX;
      // One frame per batch, kept with a cancel-and-requeue rather than a
      // has-a-frame guard: whether a second move lands before or after the
      // queued frame fires is not observable in advance, so a guard here is
      // a branch no test can pin. A registration made before the frame fires
      // still runs in that same frame, so the newest position paints on the
      // schedule the first move set.
      cancelAnimationFrame(drag.raf);
      drag.raf = requestAnimationFrame(applyFrame);
    },
    [applyFrame],
  );

  const endResize = React.useCallback(() => {
    const drag = dragRef.current;
    if (!drag) return;
    // Whether the release lands before or after its last move's frame has
    // painted is frame timing nothing can schedule, so neither the cancel
    // nor the flush may branch on it: the cancel is a no-op once the frame
    // has fired, and `applyFrame` guards itself against an already-flushed
    // position. Either way `lastWidth` holds the final position before the
    // commit below reads it.
    cancelAnimationFrame(drag.raf);
    drag.raf = 0;
    applyFrame();
    dragRef.current = null;
    document.body.style.cursor = drag.prevCursor;
    document.body.style.userSelect = drag.prevUserSelect;
    setActiveResizeColumnId(null);

    // A press that never moved has nothing to persist — committing here
    // would bake the whole table on a stray click.
    if (drag.lastWidth === drag.startWidth) return;

    const base = drag.bake ?? { ...sizingRef.current };
    base[drag.columnId] = Math.round(drag.lastWidth);
    onCommitRef.current(base);
  }, [applyFrame]);

  const clearColumnSize = React.useCallback((columnId: string) => {
    const next = { ...sizingRef.current };
    if (!(columnId in next)) return;
    delete next[columnId];
    onCommitRef.current(next);
  }, []);

  const controller = React.useMemo<ColumnResizeController>(
    () => ({ startResize, moveResize, endResize, clearColumnSize }),
    [startResize, moveResize, endResize, clearColumnSize],
  );

  return { controller, activeResizeColumnId };
}

// ---------------------------------------------------------------------------
// DataTableResizeHandle — the grabbable edge in each header cell
// ---------------------------------------------------------------------------

const HANDLE_HIT_WIDTH = 9;
const HANDLE_LINE_WIDTH = 2;
const DOUBLE_PRESS_MS = 350;
const PRESS_SLOP_PX = 3;

/**
 * Pointer-capture drag handle, so the drag keeps reporting through fast
 * pointer travel. Sits inside the header cell's right edge (cells clip
 * overflow), above the sort button, and stops propagation so a resize never
 * sorts or starts a header reorder. Two quick clicks without movement clear
 * the column's override (detected from pointer-up timing so a drag never
 * counts as a click).
 *
 * The boundary line is revealed per column, not per row: each header cell is
 * a `group/hcell` (and carries `data-dthcell`), and hovering a column shows
 * only that column's edges — its own cell's hover reveals the right edge,
 * and a `:has(+ :hover)` selector on the preceding cell reveals the left one
 * (CSS has no previous-sibling selector). The handle's own `group/resize`
 * turns the line under the pointer to the accent color. `active` keeps it
 * accent-colored during the drag, when pointer capture makes :hover
 * unreliable.
 */
export function DataTableResizeHandle({
  columnId,
  controller,
  active,
}: {
  columnId: string;
  controller: ColumnResizeController;
  active: boolean;
}): React.ReactElement {
  const downXRef = React.useRef(0);
  const movedRef = React.useRef(false);
  const lastTapAtRef = React.useRef(0);

  return (
    <Box group="resize" touchAction="none" userSelect="none"
      data-dtresize="true"
      data-testid={`data-table-resize-handle-${columnId}`}
      position="absolute"
      top={0}
      bottom={0}
      right={0}
      width={HANDLE_HIT_WIDTH}
      zIndex={20}
      cursor="col-resize"
      onPointerDown={(e: React.PointerEvent<HTMLDivElement>) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        downXRef.current = e.clientX;
        movedRef.current = false;
        controller.startResize(columnId, e.clientX);
      }}
      onPointerMove={(e: React.PointerEvent<HTMLDivElement>) => {
        if (Math.abs(e.clientX - downXRef.current) > PRESS_SLOP_PX) {
          movedRef.current = true;
        }
        controller.moveResize(e.clientX);
      }}
      onPointerUp={(e: React.PointerEvent<HTMLDivElement>) => {
        try {
          e.currentTarget.releasePointerCapture?.(e.pointerId);
        } catch {
          // releasing an uncaptured pointer throws in some browsers
        }
        controller.endResize();
        if (!movedRef.current) {
          const now = Date.now();
          if (now - lastTapAtRef.current < DOUBLE_PRESS_MS) {
            lastTapAtRef.current = 0;
            controller.clearColumnSize(columnId);
          } else {
            lastTapAtRef.current = now;
          }
        }
      }}
      onPointerCancel={() => controller.endResize()}
    >
      <Box hoverReveal="resize" pointerEvents="none" background="border"
        className={cn("",
          // Own column hovered → this line is its right edge.
          "group-hover/hcell:opacity-0",
          // Next column hovered → this line is its left edge. No CSS
          // previous-sibling selector exists, so :has() on the owning cell.
          "[[data-dthcell]:has(+[data-dthcell]:hover)_&]:opacity-0",
          "group-hover/resize:bg-ring",
          active && "bg-ring opacity-100",
        )}
        position="absolute"
        top={0}
        bottom={0}
        left={HANDLE_HIT_WIDTH - HANDLE_LINE_WIDTH - 1}
        width={HANDLE_LINE_WIDTH}
        style={{
          transition: "opacity 150ms ease, background-color 150ms ease",
        }}
      />
    </Box>
  );
}
